import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { Pagination } from '@/components/ui/Pagination';
import { useOrg } from '@/contexts/OrgContext';
import { useToast } from '@/contexts/ToastContext';
import { toastError } from '@/lib/toastErrors';
import { apiPost } from '@/lib/api';
import { getFirestoreDb } from '@/lib/firebase';
import { useWorkerHealth } from '@/hooks/useWorkerHealth';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { formatMoneyDisplay } from '@finvox/shared/money';

type RevenueTrendPoint = { month: string; amount: string };
type ExpenseByTypePoint = { type: string; amount: string };
type RecentActivityItem = { action: string; at: string };

type Stats = {
  revenueMtd: string;
  expensesMtd?: string;
  netIncomeMtd?: string;
  cashBalance: string;
  taxLiability: string;
  draftJournalCount: number;
  draftInvoiceCount: number;
  draftBillCount: number;
  openImportJobs: number;
  revenueTrend?: RevenueTrendPoint[];
  expenseByType?: ExpenseByTypePoint[];
  recentActivity?: RecentActivityItem[];
  updatedAt?: string;
};

const CHART_COLORS = ['#0d9488', '#14b8a6', '#2dd4bf', '#5eead4', '#99f6e4'];

export function DashboardPage() {
  const { t } = useTranslation();
  const chartMoney = (v: number) => formatMoneyDisplay(String(v));
  const { orgId, brandName, skipLiveUpdate } = useOrg();
  const online = useOnlineStatus();
  const toast = useToast();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [activityPage, setActivityPage] = useState(0);
  const activityPageSize = 10;
  const { ready: workerReady, showDevBanner, recheck: recheckWorker } = useWorkerHealth();

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(doc(getFirestoreDb(), 'organizations', orgId, 'dashboardStats', 'summary'), (snap) => {
      if (skipLiveUpdate(snap.metadata, orgId)) return;
      if (snap.exists()) setStats(snap.data() as Stats);
    });
  }, [orgId, skipLiveUpdate]);

  useEffect(() => {
    if (!orgId || !online || !workerReady) return;
    void apiPost('/v1/dashboard/stats', { orgId, refresh: true }).catch(() => {});
  }, [orgId, online, workerReady]);

  async function refresh() {
    if (!orgId || !online) return;
    const ready = workerReady === true ? true : await recheckWorker();
    if (!ready) {
      toast.error(t('errors.workerUnavailable'), 'dashboard-refresh');
      return;
    }
    setLoading(true);
    try {
      const res = await apiPost<{ stats: Stats }>('/v1/dashboard/stats', { orgId, refresh: true });
      if (!res.stats) {
        toast.error(t('dashboard.refreshFailed'), 'dashboard-refresh');
        return;
      }
      setStats(res.stats);
      toast.success(t('dashboard.refreshed'), 'dashboard-refresh');
    } catch (e) {
      toastError(toast, e, 'dashboard-refresh', 'dashboard.refreshFailed');
    } finally {
      setLoading(false);
    }
  }

  const cards = [
    { label: t('dashboard.revenueMtd'), value: stats?.revenueMtd ?? '—', money: true },
    { label: t('dashboard.expensesMtd'), value: stats?.expensesMtd ?? '—', money: true },
    { label: t('dashboard.netIncomeMtd'), value: stats?.netIncomeMtd ?? '—', money: true },
    { label: t('dashboard.cashBalance'), value: stats?.cashBalance ?? '—', money: true },
    { label: t('dashboard.taxLiability'), value: stats?.taxLiability ?? '—', money: true },
    { label: t('dashboard.draftJournals'), value: String(stats?.draftJournalCount ?? '—'), money: false },
    { label: t('dashboard.draftInvoices'), value: String(stats?.draftInvoiceCount ?? '—'), money: false },
    { label: t('dashboard.draftBills'), value: String(stats?.draftBillCount ?? '—'), money: false }
  ];

  const revenueChartData =
    stats?.revenueTrend?.map((p) => ({
      month: p.month,
      amount: Number.parseFloat(p.amount) || 0
    })) ?? [];

  const expenseChartData =
    stats?.expenseByType
      ?.filter((p) => Number.parseFloat(p.amount) > 0)
      .map((p) => ({
        type: t(`coa.types.${p.type}`, { defaultValue: p.type }),
        amount: Number.parseFloat(p.amount) || 0
      })) ?? [];

  const hasChartData = revenueChartData.some((d) => d.amount > 0) || expenseChartData.length > 0;

  const activity = stats?.recentActivity ?? [];
  const activitySlice = useMemo(() => {
    const start = activityPage * activityPageSize;
    return activity.slice(start, start + activityPageSize);
  }, [activity, activityPage]);
  const activityHasNext = (activityPage + 1) * activityPageSize < activity.length;
  const activityHasPrev = activityPage > 0;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('dashboard.welcome', { name: brandName })}</h1>
          {stats?.updatedAt ? (
            <p className="mt-1 text-xs text-[var(--fv-text-muted)]">
              {t('dashboard.lastUpdated')}:{' '}
              {new Date(stats.updatedAt).toLocaleString('en-US', { numberingSystem: 'latn' })}
            </p>
          ) : null}
        </div>
        <Button
          variant="secondary"
          loading={loading}
          onClick={() => void refresh()}
          disabled={!online}
        >
          {t('dashboard.refresh')}
        </Button>
      </div>

      {showDevBanner ? (
        <div
          className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          role="status"
        >
          {t('dashboard.workerBanner')}
        </div>
      ) : null}

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <Card key={c.label} className="p-5">
            <p className="text-sm text-[var(--fv-text-muted)]">{c.label}</p>
            <p className="mt-2 text-2xl font-semibold">
              {c.money ? <MoneyDisplay value={c.value} className="text-2xl font-semibold" /> : c.value}
            </p>
          </Card>
        ))}
      </div>

      {(stats?.openImportJobs ?? 0) > 0 && (
        <p className="mt-4 text-sm text-amber-700">
          {t('dashboard.importRunning', { count: stats!.openImportJobs })}
        </p>
      )}

      <div className="mt-8 grid gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <h2 className="text-sm font-semibold">{t('dashboard.revenueTrend')}</h2>
          {hasChartData && revenueChartData.length ? (
            <div className="mt-4 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenueChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--fv-border)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => chartMoney(v)} />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    stroke="var(--fv-primary)"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="mt-4 text-sm text-[var(--fv-text-muted)]">{t('dashboard.noChartData')}</p>
          )}
        </Card>

        <Card className="p-5">
          <h2 className="text-sm font-semibold">{t('dashboard.expenseBreakdown')}</h2>
          {expenseChartData.length ? (
            <div className="mt-4 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={expenseChartData}
                    dataKey="amount"
                    nameKey="type"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ type }) => type}
                  >
                    {expenseChartData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => chartMoney(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="mt-4 text-sm text-[var(--fv-text-muted)]">{t('dashboard.noChartData')}</p>
          )}
        </Card>
      </div>

      <Card className="mt-4 p-5">
        <h2 className="text-sm font-semibold">{t('dashboard.recentActivity')}</h2>
        <ul className="mt-3 space-y-2 text-sm">
          {activitySlice.map((a, i) => (
            <li
              key={`${activityPage}-${i}`}
              className="flex flex-wrap justify-between gap-2 border-b border-[var(--fv-border)] pb-2 last:border-0"
            >
              <span>{a.action}</span>
              <span className="text-[var(--fv-text-muted)]">
                {a.at ? new Date(a.at).toLocaleString('en-US', { numberingSystem: 'latn' }) : '—'}
              </span>
            </li>
          ))}
          {!activity.length && (
            <li className="text-[var(--fv-text-muted)]">{t('settings.noAudit')}</li>
          )}
        </ul>
        {activity.length > activityPageSize && (
          <Pagination
            className="mt-2 border-0 px-0"
            pageIndex={activityPage}
            hasNext={activityHasNext}
            hasPrev={activityHasPrev}
            onNext={() => setActivityPage((p) => p + 1)}
            onPrev={() => setActivityPage((p) => Math.max(0, p - 1))}
          />
        )}
      </Card>
    </div>
  );
}
